<script setup>
import { computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

function humanize(slug) {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

const entitlements = computed(() => getters['plan/getEntitlements'].value);
const plans = computed(() => getters['plan/getPlans'].value);
const subscription = computed(() => getters['plan/getSubscription'].value);
const uiFlags = computed(() => getters['plan/getUIFlags'].value);

const currentSlug = computed(() => entitlements.value.plan?.slug);
const hasSubscription = computed(() => subscription.value.status !== 'none');

const meters = computed(() =>
  Object.entries(entitlements.value.meters).map(([metric, data]) => {
    const hasLimit = data.limit !== null && data.limit !== undefined;
    const limitLabel = hasLimit ? data.limit : t('PLAN_SETTINGS.UNLIMITED');
    return {
      metric,
      label: humanize(metric),
      display: `${data.usage} / ${limitLabel}`,
      percent:
        hasLimit && data.limit > 0
          ? Math.min(100, Math.round((data.usage / data.limit) * 100))
          : 0,
    };
  })
);

function formatPrice(plan) {
  if (!plan.price_cents) return t('PLAN_SETTINGS.PRICE_FREE');
  return t('PLAN_SETTINGS.PRICE', {
    amount: (plan.price_cents / 100).toFixed(0),
    interval: plan.interval,
  });
}

const isCurrent = plan => plan.slug === currentSlug.value;

const canUpgrade = plan =>
  entitlements.value.billingEnabled && plan.purchasable && !isCurrent(plan);

// There is no switch-in-place any more. Setting a plan directly used to be
// offered wherever billing was unconfigured, which made a customer's ability to
// grant themselves any plan depend on an environment variable; the endpoint is
// gone. Paid moves go through checkout or the billing portal, and operator
// grants through Super Admin.

const upgrade = plan => store.dispatch('plan/checkout', plan.slug);

const manageBilling = () => store.dispatch('plan/openPortal');

onBeforeMount(() => {
  store.dispatch('plan/fetch');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('PLAN_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PLAN_SETTINGS.TITLE')"
        :description="$t('PLAN_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 mt-4">
        <!-- Current plan -->
        <div
          class="flex items-center justify-between p-4 border border-n-weak rounded-xl bg-n-solid-1"
        >
          <div class="flex flex-col gap-1">
            <span class="text-body-small text-n-slate-11">
              {{ $t('PLAN_SETTINGS.CURRENT_PLAN') }}
            </span>
            <span class="text-heading-2 text-n-slate-12">
              {{ entitlements.plan?.name || '—' }}
            </span>
            <span class="text-body-small text-n-slate-11 capitalize">
              {{
                $t('PLAN_SETTINGS.STATUS_LABEL', {
                  status: subscription.status,
                })
              }}
            </span>
          </div>
          <Button
            v-if="entitlements.billingEnabled && hasSubscription"
            :label="$t('PLAN_SETTINGS.MANAGE_BILLING')"
            variant="outline"
            size="sm"
            @click="manageBilling"
          />
        </div>

        <!-- Unlocked features -->
        <div class="flex flex-col gap-2">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('PLAN_SETTINGS.FEATURES') }}
          </span>
          <div v-if="entitlements.features.length" class="flex flex-wrap gap-2">
            <span
              v-for="feature in entitlements.features"
              :key="feature"
              class="px-2.5 py-1 text-body-small rounded-lg bg-n-alpha-2 text-n-slate-12"
            >
              {{ humanize(feature) }}
            </span>
          </div>
          <span v-else class="text-body-main text-n-slate-11">
            {{ $t('PLAN_SETTINGS.NO_FEATURES') }}
          </span>
        </div>

        <!-- Usage -->
        <div class="flex flex-col gap-3">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('PLAN_SETTINGS.USAGE') }}
          </span>
          <div
            v-for="meter in meters"
            :key="meter.metric"
            class="flex flex-col gap-1"
          >
            <div class="flex items-center justify-between text-body-small">
              <span class="text-n-slate-12">{{ meter.label }}</span>
              <span class="text-n-slate-11">{{ meter.display }}</span>
            </div>
            <div class="w-full h-2 rounded-full bg-n-alpha-2 overflow-hidden">
              <div
                class="h-full rounded-full bg-n-brand"
                :style="{ width: `${meter.percent}%` }"
              />
            </div>
          </div>
        </div>

        <!-- Available plans -->
        <div class="flex flex-col gap-3">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('PLAN_SETTINGS.AVAILABLE_PLANS') }}
          </span>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div
              v-for="availablePlan in plans"
              :key="availablePlan.slug"
              class="flex flex-col gap-3 p-4 border rounded-xl"
              :class="
                isCurrent(availablePlan)
                  ? 'border-n-brand bg-n-alpha-1'
                  : 'border-n-weak'
              "
            >
              <div class="flex flex-col gap-1">
                <span class="text-heading-3 text-n-slate-12">
                  {{ availablePlan.name }}
                </span>
                <span class="text-body-main text-n-slate-11">
                  {{ formatPrice(availablePlan) }}
                </span>
              </div>
              <span class="text-body-small text-n-slate-11">
                {{
                  $t('PLAN_SETTINGS.FEATURE_COUNT', {
                    count: availablePlan.features.length,
                  })
                }}
              </span>
              <Button
                v-if="isCurrent(availablePlan)"
                :label="$t('PLAN_SETTINGS.CURRENT')"
                variant="outline"
                size="sm"
                disabled
              />
              <Button
                v-else-if="canUpgrade(availablePlan)"
                :label="$t('PLAN_SETTINGS.UPGRADE')"
                size="sm"
                :is-loading="uiFlags.isUpdating"
                @click="upgrade(availablePlan)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
